import { Button, styled } from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import React from 'react';

import KebabMenu from 'components/kebab-menu';
import ChartThemeScope from 'components/charts/theme/chart-theme-scope';
import { useChartMode } from 'components/charts/hooks/use-chart-mode';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import { ChartSurfaceProps } from 'components/charts/types/chart-surface.type';

export interface ChartLegendVisibilityMenuProps extends ChartSurfaceProps {
  visible: boolean;
  onToggle: () => void;
  title: string;
  hideLabel: string;
  showLabel: string;
}

const MenuItem = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'chartPalette' && prop !== 'popoverDark',
})<{ chartPalette: ChartPalette; popoverDark: boolean }>(({ theme, chartPalette, popoverDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(2),
  color: chartPalette.text.primary,
  textTransform: 'none',
  padding: theme.spacing(2),
  ...(popoverDark && {
    borderRadius: theme.shape.borderRadius,
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: theme.palette.action.hover,
      color: chartPalette.text.primary,
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${chartPalette.text.primary}`,
      outlineOffset: 2,
    },
  }),
}));

const ChartLegendVisibilityMenu: React.FC<ChartLegendVisibilityMenuProps> = ({
  visible,
  onToggle,
  title,
  hideLabel,
  showLabel,
  dark,
}) => {
  const palette = useChartPalette(dark);
  const mode = useChartMode(dark);

  return (
    <ChartThemeScope dark={dark}>
      {/* KebabMenu's `dark` means a dark icon, which suits a light surface. */}
      <KebabMenu title={title} dark={mode === 'light'} popoverDark={mode === 'dark'}>
        <MenuItem
          chartPalette={palette}
          popoverDark={mode === 'dark'}
          variant="text"
          startIcon={visible ? <EyeOff size={16} /> : <Eye size={16} />}
          onClick={onToggle}
        >
          {visible ? hideLabel : showLabel}
        </MenuItem>
      </KebabMenu>
    </ChartThemeScope>
  );
};

export default ChartLegendVisibilityMenu;
